// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

// Single source of truth for role-gated UI. Everything that used to call
// hasKeycloakRole(user?.token, ...) directly goes through here, so the developer/admin
// "view as role" override in stores/roleOverrideState applies everywhere at once.

import { useMemo } from 'react';

import type { UserSession } from '@/app/user-context';
import { useRoleOverrideState, type RoleOverride, type ViewAsRole } from '@/stores/roleOverrideState';
import { hasKeycloakRole } from '@/utils/keycloakRoles';

export type EffectiveRoles = {
  /** Effective developer role — gate UI with this. */
  isDeveloper: boolean;
  /** Effective admin role — gate UI with this. */
  isAdmin: boolean;
  /** Effective `user` role: any signed-in account, paid or not. */
  isUser: boolean;
  /** Effective `user-paid` role. Nothing is gated on it yet. */
  isPaidUser: boolean;
  /** Developer role as it stands in the token, ignoring any simulation. */
  isRealDeveloper: boolean;
  /** Admin role as it stands in the token, ignoring any simulation. */
  isRealAdmin: boolean;
  /**
   * True for a real developer or real admin. Gate the role picker itself with this, so
   * simulating "player" can never lock a developer or admin out.
   */
  canViewAs: boolean;
  /** The simulated role, when one is in effect. */
  viewAsRole: ViewAsRole | null;
};

/**
 * Exact-role simulation for the privileged roles: picking Developer does not grant Admin, nor the
 * reverse. `user` is the exception in both directions — every signed-in account carries it, and a
 * paid account carries `user-paid` on top, so it is baseline for everything except "player", which
 * exists precisely to render the app as a token with no roles at all.
 */
const SIMULATED: Record<ViewAsRole, Omit<EffectiveRoles, 'isRealDeveloper' | 'isRealAdmin' | 'canViewAs' | 'viewAsRole'>> = {
  developer: { isDeveloper: true, isAdmin: false, isUser: true, isPaidUser: false },
  admin: { isDeveloper: false, isAdmin: true, isUser: true, isPaidUser: false },
  'user-paid': { isDeveloper: false, isAdmin: false, isUser: true, isPaidUser: true },
  user: { isDeveloper: false, isAdmin: false, isUser: true, isPaidUser: false },
  player: { isDeveloper: false, isAdmin: false, isUser: false, isPaidUser: false },
};

export const resolveEffectiveRoles = (
  token: string | null | undefined,
  userId: string | null | undefined,
  override: RoleOverride | null
): EffectiveRoles => {
  const isRealDeveloper = hasKeycloakRole(token, 'developer');
  const isRealAdmin = hasKeycloakRole(token, 'admin');
  const canViewAs = isRealDeveloper || isRealAdmin;
  // Scoped to the user it was picked for, and only ever honoured for a real developer or
  // admin — a hand-written storage entry grants nobody anything.
  const scoped = override && userId && override.userId === userId ? override.role : null;
  const viewAsRole = canViewAs ? scoped : null;
  if (!viewAsRole) {
    // `user-paid` does not satisfy a check for `user` on its own — hasKeycloakRole matches on
    // exact names and `-`-separated suffixes, and "user-paid" ends with neither — so ask for both.
    const isPaidUser = hasKeycloakRole(token, 'user-paid');
    return {
      isDeveloper: isRealDeveloper,
      isAdmin: isRealAdmin,
      isUser: isPaidUser || hasKeycloakRole(token, 'user'),
      isPaidUser,
      isRealDeveloper,
      isRealAdmin,
      canViewAs,
      viewAsRole: null,
    };
  }
  return { ...SIMULATED[viewAsRole], isRealDeveloper, isRealAdmin, canViewAs, viewAsRole };
};

export const useEffectiveRoles = (user: UserSession | null | undefined): EffectiveRoles => {
  const override = useRoleOverrideState((state) => state.override);
  return useMemo(() => resolveEffectiveRoles(user?.token, user?.id, override), [override, user?.id, user?.token]);
};
